import { Component } from '@theme/component';

const GIFT_LINE_PROPERTY = '_mova_gift';
const GIFT_UPDATE_EVENT = 'mova-gift:update';

/**
 * Picks which standalone gift product(s) go with the current purchase
 * option. The gift is added to the cart as its own line item by the PDP's
 * add-to-cart script — this component only resolves *which* gift variant(s)
 * were chosen and whether the cart may already have one, since the default
 * store policy is a single gift per order no matter how many products or
 * purchase options are added.
 *
 * Progressive mode (data-progressive="true", opt-in per section): the gift
 * chosen at any quantity carries over as the
 * shopper buys more — switching between 1/2/3 units never resets it, since
 * every tier shares the same gift pool (data-source is uniformly
 * "progressive", see product-gift-choice.liquid). From
 * data-second-gift-tier units up, a second, different gift unlocks —
 * excluded from its own choices is whichever gift was already picked
 * first. Order-summary panels read this component's state via a
 * `mova-gift:update` CustomEvent instead of polling.
 * @extends Component<{dialog: HTMLDialogElement, confirm: HTMLButtonElement, selection: HTMLDivElement | undefined, preview: HTMLDivElement | undefined, status: HTMLParagraphElement | undefined, title: HTMLHeadingElement, description: HTMLParagraphElement}>
 */
class GiftChoice extends Component {
  requiredRefs = ['dialog', 'confirm', 'title', 'description'];
  #controller;
  #source = '';
  #selected = '';
  #returnFocus;
  #tier = 0;
  #primary = '';
  #secondary = '';
  #pendingSlot = 'primary';
  #cartGiftVariantIds = [];

  get form() {
    const form = document.getElementById(this.dataset.formId);
    return form instanceof HTMLFormElement ? form : null;
  }

  get section() { return this.closest('.mova-pdp-V5'); }
  get variantInput() { return this.form?.querySelector('input[name="id"]'); }
  get options() { return [...this.querySelectorAll('[data-gift-option]')]; }
  get isProgressive() { return this.dataset.progressive === 'true'; }
  get secondGiftTier() { return Number(this.dataset.secondGiftTier) || 3; }
  get configuredGiftLimit() {
    const fixedLimit = Number(this.dataset.giftLimit) || 1;
    const globalPicker = document.querySelector('mova-gift-picker');
    if (typeof globalPicker?.giftLimitFor === 'function') {
      return Math.max(fixedLimit, globalPicker.giftLimitFor(this.dataset.productId));
    }

    const globalLimit = [...document.querySelectorAll('mova-gift-picker [data-gift-rule]')]
      .filter((rule) => rule.dataset.productId === String(this.dataset.productId || ''))
      .reduce((limit, rule) => Math.max(limit, Number(rule.dataset.giftLimit) || 1), 1);
    return Math.max(fixedLimit, globalLimit);
  }
  get isMultiGift() { return this.isProgressive || this.configuredGiftLimit > 1; }
  get allowedGiftCount() {
    if (this.isProgressive) return this.#tier >= this.secondGiftTier ? 2 : 1;
    return this.configuredGiftLimit;
  }
  get selectionStorageKey() { return `mova:gift-choice:v2:${this.dataset.productId || 'product'}`; }

  /** The primary gift variant id — chosen once, applies at every quantity. */
  get selectedGiftVariantId() { return this.isMultiGift ? this.#primary : this.#selected; }
  /** The bonus second gift variant id (progressive mode, higher tiers only). */
  get selectedSecondaryGiftVariantId() { return this.#secondary; }
  /** The current quantity tier (progressive mode), for order-summary panels. */
  get currentTier() { return this.#tier; }

  /**
   * Gift variants that can still be added without exceeding the allowance
   * already consumed by this product's gift lines in the cart.
   */
  get selectedGiftVariantIdsForCart() {
    const allowedCount = this.allowedGiftCount;
    const remainingCount = Math.max(0, allowedCount - this.#cartGiftVariantIds.length);
    const existingVariantIds = new Set(this.#cartGiftVariantIds);
    const selectedVariantIds = [this.#primary, this.#secondary]
      .filter(Boolean)
      .filter((variantId, index, values) => values.indexOf(variantId) === index)
      .filter((variantId) => !existingVariantIds.has(String(variantId)));

    return selectedVariantIds.slice(0, remainingCount);
  }

  connectedCallback() {
    super.connectedCallback();
    this.#connect();
  }

  updatedCallback() {
    super.updatedCallback();
    // Section morphs and sibling UI updates (for example the coupon state)
    // must refresh refs/listeners without erasing an already confirmed gift.
    // Cart verification below remains the authority for consumed slots.
    this.#connect();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    if (this.refs.dialog.open) this.refs.dialog.close();
    super.disconnectedCallback();
  }

  #connect() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.section?.addEventListener('click', this.#optionChanged, { signal });
    this.section?.addEventListener('change', this.#optionChanged, { signal });
    for (const card of this.section?.querySelectorAll('.purchase-card:not(.qty-select-card)') || []) {
      card.setAttribute('role', 'button');
      card.setAttribute('tabindex', '0');
      card.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        card.click();
      }, { signal });
    }
    this.refs.dialog.addEventListener('close', () => {
      if (this.#returnFocus?.isConnected) this.#returnFocus.focus();
    }, { signal });
    // Em modo progressivo o pop-up só abre quando uma escolha é obrigatória
    // — Esc (que dispara 'cancel' antes do 'close') não pode fechar sem
    // escolher, senão o resumo do pedido fica com o brinde vazio.
    this.refs.dialog.addEventListener('cancel', (event) => {
      if (this.isProgressive) event.preventDefault();
    }, { signal });
    if (this.isProgressive) {
      this.#restoreProgressiveSelection();
      this.#syncProgressive();
      this.refreshCartGiftState().then(() => this.#notifyUpdate()).catch(() => {});
    } else {
      this.#sync();
      if (this.isMultiGift) {
        const restoreCartGifts = () => {
          this.refreshCartGiftState().then(() => this.#notifyUpdate()).catch(() => {});
        };
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', restoreCartGifts, { once: true, signal });
        } else {
          restoreCartGifts();
        }
      }
    }
  }

  #sync() {
    const active = this.section?.querySelector('.option-selectable.featured-active, .option-selectable.active-style');
    for (const card of this.section?.querySelectorAll('.purchase-card[role="button"]') || []) {
      card.setAttribute('aria-pressed', String(card === active));
    }
    // Dentro do mesmo cartão de mistura de sabores (qty-select-card), cada
    // combinação resolve pra uma variante diferente — mas o brinde vale pra
    // quantidade escolhida, não pra combinação exata. Usar o grupo como
    // fonte aqui evita perder o brinde já confirmado só por trocar o sabor
    // de uma unidade (ver mesma chave em product-gift-choice.liquid).
    const source = active?.classList.contains('qty-select-card')
      ? `qtygroup:${active.dataset.qtyGroup || ''}`
      : active?.dataset.variantId || this.variantInput?.value || '';
    if (source !== this.#source) {
      this.#source = source;
      this.#selected = '';
      this.#primary = '';
      this.#secondary = '';
      if (this.refs.selection) this.refs.selection.hidden = true;
      this.refs.preview?.replaceChildren();
      if (this.refs.status) this.refs.status.textContent = '';
      for (const input of this.querySelectorAll('input[type="radio"]')) input.checked = false;
    }
    let count = 0;
    for (const option of this.options) {
      const matches = option.dataset.source === source;
      option.hidden = !matches;
      option.querySelector('input').disabled = !matches;
      if (matches) count++;
    }
    return count > 0;
  }

  /**
   * Progressive mode's sync: the gift pool never depends on which offer is
   * active (data-source is uniformly "progressive"), so the only thing
   * that changes per selection is the quantity tier. Returns whether the
   * tier actually changed, so callers only act (open a dialog, drop a
   * now-irrelevant second gift) on a real transition.
   */
  #syncProgressive() {
    const active = this.section?.querySelector('.option-selectable.featured-active, .option-selectable.active-style');
    for (const card of this.section?.querySelectorAll('.purchase-card[role="button"]') || []) {
      card.setAttribute('aria-pressed', String(card === active));
    }
    const tier = Number(active?.dataset.quantity) || 0;
    const tierChanged = tier !== this.#tier;
    this.#tier = tier;
    for (const option of this.options) {
      option.hidden = false;
      option.querySelector('input').disabled = false;
    }
    if (tierChanged && this.#tier < this.secondGiftTier && this.#secondary) {
      this.#secondary = '';
      this.#persistProgressiveSelection();
    }
    return tierChanged;
  }

  #optionChanged = async (event) => {
    if (!(event.target instanceof Element) || this.contains(event.target)) return;
    if (event.target.closest('mova-yampi-coupon, .order-summary, .gift-cards-shell, .product-form__buttons')) return;
    if (!event.target.closest('.option-selectable, .flavor-item')) return;
    // Native selects must finish their change before the modal takes focus.
    if (event.type === 'click' && event.target.closest('select')) return;

    if (this.isProgressive) {
      const tierChanged = this.#syncProgressive();
      if (!tierChanged) return;
      let cartGiftState;
      try {
        cartGiftState = await this.refreshCartGiftState();
      } catch {
        return;
      }
      this.#notifyUpdate();
      if (this.options.length === 0) return;
      if (cartGiftState.count === 0 && !this.#primary) {
        this.#open('primary');
      } else if (
        this.#tier >= this.secondGiftTier &&
        cartGiftState.count < cartGiftState.allowedCount &&
        !this.#secondary
      ) {
        this.#open('secondary');
      }
      return;
    }

    if (this.isMultiGift) {
      const previous = this.#source;
      if (!this.#sync() || previous === this.#source) return;
      let cartGiftState;
      try {
        cartGiftState = await this.refreshCartGiftState();
      } catch {
        return;
      }
      if (cartGiftState.count < cartGiftState.allowedCount && !this.#primary) this.#open('primary');
      return;
    }

    // Picking an offer is the trigger: if it requires a gift that hasn't
    // been chosen yet, the dialog opens right away — the same rule for
    // every offer (1, 2 or 3 units), so none of them need a separate
    // buy-button click just to find out a gift is required. Skipped
    // entirely when the cart already carries a gift, since the store only
    // ever grants one per order.
    const previous = this.#source;
    if (this.#sync() && previous !== this.#source && !(await this.cartAlreadyHasGift())) this.#open('primary');
  };

  /**
   * Whether the currently selected offer requires a gift choice that hasn't
   * been confirmed yet. Shared by any buy/add action so every button gates
   * on the same source of truth instead of duplicating the rule. In
   * progressive mode, every unlocked slot must either already exist in the
   * cart or have a confirmed selection. This prevents both missing and
   * duplicated gifts after a reload.
   */
  needsGiftChoice() {
    if (this.isMultiGift) {
      if (this.isProgressive) this.#restoreProgressiveSelection();
      if (this.isProgressive) this.#syncProgressive();
      else this.#sync();
      if (this.options.length === 0) return false;
      const consumedCount = Math.min(this.#cartGiftVariantIds.length, this.allowedGiftCount);
      const confirmedCount = this.selectedGiftVariantIdsForCart.length;
      return consumedCount + confirmedCount < this.allowedGiftCount;
    }
    return this.#sync() && !this.#selected;
  }

  /** Opens the gift dialog for an external buy/add action that needs the user to choose first. */
  requestGiftChoice() {
    const needsSecondary = this.isMultiGift && (
      this.#cartGiftVariantIds.length > 0 || Boolean(this.#primary)
    ) && this.allowedGiftCount > 1;
    this.#open(needsSecondary ? 'secondary' : 'primary');
  }

  /** Clears a choice after it has been successfully committed to the cart. */
  resetConfirmedSelection() {
    this.#selected = '';
    this.#primary = '';
    this.#secondary = '';
    this.#pendingSlot = 'primary';
    for (const input of this.querySelectorAll('input[type="radio"]')) input.checked = false;
    try {
      sessionStorage.removeItem(this.selectionStorageKey);
    } catch {
      // The in-memory state was still cleared.
    }
  }

  /** True when the cart already has a gift line — the store caps it at one per order. */
  async cartAlreadyHasGift() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      return cart.items.some((item) => item.properties && item.properties[GIFT_LINE_PROPERTY]);
    } catch {
      return false;
    }
  }

  /**
   * Reads the live cart and restores the progressive gift slots already
   * consumed by this product. Throws when the cart cannot be verified so
   * callers can fail closed instead of granting a duplicate gift.
   */
  async refreshCartGiftState() {
    const response = await fetch('/cart.js', {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('Unable to verify cart gifts');

    const cart = await response.json();
    const productId = String(this.dataset.productId || '');
    const configuredVariantIds = new Set(
      String(this.dataset.triggerVariantIds || '').split(',').map((variantId) => variantId.trim()).filter(Boolean)
    );
    const qualifyingVariantIds = new Set(
      cart.items
        .filter((item) => !(item.properties && item.properties[GIFT_LINE_PROPERTY]))
        .filter((item) => configuredVariantIds.size > 0
          ? configuredVariantIds.has(String(item.variant_id))
          : (!productId || String(item.product_id) === productId))
        .map((item) => String(item.variant_id))
    );

    const cartGiftVariantIds = [];
    for (const item of cart.items) {
      if (!(item.properties && item.properties[GIFT_LINE_PROPERTY])) continue;
      const triggerVariantId = String(item.properties._mova_gift_for || '');
      if (this.isProgressive && !qualifyingVariantIds.has(triggerVariantId)) continue;

      const quantity = Math.max(1, Number(item.quantity) || 1);
      for (let index = 0; index < quantity; index++) {
        cartGiftVariantIds.push(String(item.variant_id));
      }
    }

    this.#cartGiftVariantIds = cartGiftVariantIds;

    if (this.isMultiGift) {
      const optionVariantIds = new Set(
        this.options.map((option) => String(option.querySelector('input')?.value || '')).filter(Boolean)
      );
      const selectableCartGifts = cartGiftVariantIds.filter((variantId) => optionVariantIds.has(variantId));
      if (!this.#primary && selectableCartGifts[0]) this.#primary = selectableCartGifts[0];
      if (!this.#secondary && selectableCartGifts[1]) this.#secondary = selectableCartGifts[1];
    }

    return {
      count: cartGiftVariantIds.length,
      variantIds: [...cartGiftVariantIds],
      allowedCount: this.allowedGiftCount,
    };
  }

  #open(slot) {
    this.#pendingSlot = slot;
    const isSecondary = slot === 'secondary';
    this.refs.title.textContent = isSecondary
      ? this.refs.title.dataset.titleSecondary || this.refs.title.dataset.titlePrimary
      : this.refs.title.dataset.titlePrimary;
    this.refs.description.textContent = isSecondary
      ? this.refs.description.dataset.descriptionSecondary || this.refs.description.dataset.descriptionPrimary
      : this.refs.description.dataset.descriptionPrimary;
    for (const option of this.options) {
      const input = option.querySelector('input');
      const excluded = isSecondary && (
        input.value === this.#primary || this.#cartGiftVariantIds.includes(String(input.value))
      );
      option.toggleAttribute('data-excluded', excluded);
      input.disabled = excluded;
      if (excluded) input.checked = false;
    }
    this.selectGift();
    if (!this.refs.dialog.open) {
      this.#returnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      this.refs.dialog.showModal();
    }
  }

  /** Native dialog close preserves Escape handling and focus restoration. */
  closeDialog() { this.refs.dialog.close(); }

  /** Updates confirmation availability after a radio change. */
  selectGift() {
    this.refs.confirm.disabled = !this.querySelector('[data-gift-option]:not([hidden]) input:checked');
  }

  /** Records the chosen gift variant(s); adding them to the cart remains a separate, explicit action. */
  confirmGift() {
    const chosen = this.querySelector('[data-gift-option]:not([hidden]) input:checked');
    if (!(chosen instanceof HTMLInputElement)) return;

    if (this.isMultiGift) {
      if (this.#pendingSlot === 'secondary') {
        this.#secondary = chosen.value;
        this.#persistProgressiveSelection();
        this.closeDialog();
        this.#notifyUpdate();
        return;
      }
      this.#primary = chosen.value;
      this.#persistProgressiveSelection();
      this.#notifyUpdate();
      // Quem carrega a página e já seleciona 3 unidades direto (sem passar
      // por 1 e 2) só recebe UM evento de mudança de nível — sem este
      // check, o 2º brinde nunca seria oferecido nesse caminho.
      if (this.allowedGiftCount > 1 && !this.#secondary) {
        this.#open('secondary');
      } else {
        this.closeDialog();
      }
      return;
    }

    this.#selected = chosen.value;
    if (this.refs.status) this.refs.status.textContent = this.refs.status.dataset.label;
    const sourceImage = chosen.closest('[data-gift-option]')?.querySelector('img');
    if (sourceImage instanceof HTMLImageElement && this.refs.preview) {
      const preview = sourceImage.cloneNode(true);
      if (preview instanceof HTMLImageElement) {
        preview.alt = chosen.getAttribute('aria-label') || '';
        this.refs.preview.replaceChildren(preview);
      }
    }
    if (this.refs.selection) this.refs.selection.hidden = false;
    this.closeDialog();
  }

  #persistProgressiveSelection() {
    if (!this.isProgressive) return;
    try {
      sessionStorage.setItem(this.selectionStorageKey, JSON.stringify({
        primary: this.#primary,
        secondary: this.#secondary,
        savedAt: Date.now(),
      }));
    } catch {
      // The in-memory choice remains valid when storage is unavailable.
    }
  }

  #restoreProgressiveSelection() {
    if (!this.isProgressive || this.#primary) return;
    try {
      const stored = JSON.parse(sessionStorage.getItem(this.selectionStorageKey) || 'null');
      if (!stored || Date.now() - Number(stored.savedAt || 0) > 2 * 60 * 60 * 1000) {
        sessionStorage.removeItem(this.selectionStorageKey);
        return;
      }

      const optionVariantIds = new Set(
        this.options.map((option) => String(option.querySelector('input')?.value || '')).filter(Boolean)
      );
      const primary = String(stored.primary || '');
      const secondary = String(stored.secondary || '');
      if (optionVariantIds.has(primary)) this.#primary = primary;
      if (optionVariantIds.has(secondary) && secondary !== primary) this.#secondary = secondary;
    } catch {
      // Ignore malformed or unavailable session storage.
    }
  }

  /** Tells order-summary panels (and anything else listening) that the gift/tier state changed. */
  #notifyUpdate() {
    this.dispatchEvent(new CustomEvent(GIFT_UPDATE_EVENT, {
      bubbles: true,
      detail: {
        tier: this.#tier,
        primary: this.#primary,
        secondary: this.#secondary,
        primaryLabel: this.#labelFor(this.#primary),
        secondaryLabel: this.#labelFor(this.#secondary),
        primaryImage: this.#imageFor(this.#primary),
        secondaryImage: this.#imageFor(this.#secondary),
      },
    }));
  }

  #labelFor(variantId) {
    if (!variantId) return '';
    const input = this.querySelector(`[data-gift-option] input[value="${variantId}"]`);
    return input?.getAttribute('aria-label') || '';
  }

  #imageFor(variantId) {
    if (!variantId) return '';
    const input = this.querySelector(`[data-gift-option] input[value="${variantId}"]`);
    return input?.closest('[data-gift-option]')?.querySelector('img')?.getAttribute('src') || '';
  }
}

if (!customElements.get('mova-gift-choice')) customElements.define('mova-gift-choice', GiftChoice);
